import Button from "components/Button";
import Grid from "components/Grid";
import Loader from "components/Loader";
import Modal from "components/Modal";
import Text from "components/Text";
import TextInput from "components/TextInput";
import { useFormik } from "formik";
import { FC } from "react";
import { colors } from "theme/colors";
import * as Yup from "yup";
import { DeleteButton, RightContentWrapper } from "./Settings.styled";
import { useSetting } from "./useSettings";

export const Settings: FC = () => {
    const {
        handleDeleteAccount,
        isModalOpen,
        handleOpenModal,
        handleCloseModal,
        isDeleting,
        isSuccess,
        isError,
        isChangePasswordLoading,
        handleSubmit,
    } = useSetting();
    const formik = useFormik({
        initialValues: {
            current_password: "",
            new_password: "",
            password_confirmation: "",
        },
        validationSchema: Yup.object({
            current_password: Yup.string().required(
                "Current password is required"
            ),
            new_password: Yup.string()
                .required("New password is required")
                .min(8, "Password must be at least 8 characters")
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    "Password must include uppercase, lowercase, number, and special character"
                ),
            password_confirmation: Yup.string()
                .required("Confirm password is required")
                .oneOf([Yup.ref("new_password"), null], "Passwords must match"),
        }),
        onSubmit: (values) => {
            handleSubmit(values);
        },
    });
    return (
        <RightContentWrapper>
            <Text variant="heading5" fontWeight="700" color={colors.grey.dark}>
                Account Settings
            </Text>
            <form onSubmit={formik.handleSubmit}>
                <Grid row={true} columnGap={2} marginTop={1} expanded>
                    <TextInput
                        name="current_password"
                        type="password"
                        label="Current Password"
                        required
                        placeholder="Enter current password"
                        labelColor={colors.text}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMsg={
                            formik.touched.current_password &&
                            formik.errors.current_password
                                ? formik.errors.current_password
                                : ""
                        }
                    />
                </Grid>
                <Grid row={true} columnGap={2} marginTop={1} expanded>
                    <TextInput
                        name="new_password"
                        type="password"
                        label="New Password"
                        required
                        placeholder="Enter new password"
                        labelColor={colors.text}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMsg={
                            formik.touched.new_password &&
                            formik.errors.new_password
                                ? formik.errors.new_password
                                : ""
                        }
                    />
                </Grid>
                <Grid
                    row={true}
                    columnGap={2}
                    marginTop={1}
                    marginBottom={1}
                    expanded
                >
                    <TextInput
                        name="password_confirmation"
                        type="password"
                        label="Confirm Password"
                        required
                        placeholder="Enter confirm password"
                        labelColor={colors.text}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMsg={
                            formik.touched.password_confirmation &&
                            formik.errors.password_confirmation
                                ? formik.errors.password_confirmation
                                : ""
                        }
                    />
                </Grid>

                <Grid row={true} rowGap={1}>
                    <Button type="submit">
                        Save Changes{" "}
                        {isChangePasswordLoading && <Loader size={28} />}
                    </Button>
                </Grid>
            </form>
            <Grid row={true} marginTop={3} marginBottom={1} rowGap={1}>
                <DeleteButton variant="outlined" onClick={handleOpenModal}>
                    Delete Account
                </DeleteButton>
            </Grid>

            <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
                <Modal.Header>
                    <Text variant="heading5" color="black">
                        Confirm Account Deletion
                    </Text>
                </Modal.Header>

                <Modal.Content>
                    {isSuccess ? (
                        <Text variant="body1">
                            Your account has been deleted successfully.
                        </Text>
                    ) : isError ? (
                        <Text variant="body1" color="red">
                            There was an error deleting your account. Please try
                            again.
                        </Text>
                    ) : (
                        <Text variant="body1">
                            We'd be sad to see you leave, but you're free to
                            delete your account at any time. Please note, once
                            it's deleted, it cannot be recovered.
                        </Text>
                    )}
                </Modal.Content>

                <Modal.Footer>
                    {!isSuccess && (
                        <DeleteButton
                            onClick={handleDeleteAccount}
                            variant="outlined"
                            disabled={isDeleting}
                        >
                            {isDeleting
                                ? "Deleting..."
                                : "Yes, Delete My Account"}
                        </DeleteButton>
                    )}
                </Modal.Footer>
            </Modal>
        </RightContentWrapper>
    );
};
